import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ArrowLeft, Zap, Download, FileText, Package, Calendar,
  CheckCircle, ExternalLink, AlertTriangle, MessageSquare, Send, Paperclip, Upload,
} from "lucide-react";
import { STATUS_LABELS, STATUS_COLORS } from "../lib/status";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const customerAxios = () => {
  const session = localStorage.getItem("lf_customer_session") || "";
  return axios.create({
    withCredentials: true,
    headers: session ? { "X-Customer-Session": session } : {},
  });
};

const EUR_RATE = 0.92;
const fmt = (n, c) => c === "eur" ? `€${(n).toFixed(2)}` : `$${(n || 0).toFixed(2)}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
const fmtSize = (b) => {
  if (!b) return "—";
  if (b > 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
};

function downloadFromBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function InvoiceRow({ kind, item, currency, onDownload, downloading }) {
  const amount = kind === "transaction" ? item.amount : (item.currency === "eur" ? item.amount_usd * EUR_RATE : item.amount_usd);
  const cur = item.currency || currency || "usd";
  const label = kind === "transaction"
    ? ({
        upfront_50: "50% Deposit",
        final_50: "Final 50%",
        additional_charge: "Additional Scope",
      }[item.payment_type] || "Payment")
    : "Additional Charge";
  const date = kind === "transaction" ? (item.updated_at || item.created_at) : (item.paid_at || item.created_at);
  const id = kind === "transaction" ? item.transaction_id : item.charge_id;
  return (
    <div data-testid={`invoice-row-${id}`} className="flex items-center justify-between gap-3 py-3 border-b border-zinc-100 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-zinc-950">{fmt(amount, cur)} <span className="text-[10px] uppercase font-normal text-zinc-500">{cur}</span></div>
        <div className="text-xs text-zinc-600 mt-0.5">{label}{kind === "charge" ? ` — ${item.description}` : ""}</div>
        <div className="text-[11px] text-zinc-400 mt-0.5 inline-flex items-center gap-1"><Calendar size={10} /> Paid {fmtDate(date)}</div>
      </div>
      <button data-testid={`btn-download-invoice-${id}`} onClick={() => onDownload(kind, id)} disabled={downloading === id}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
        {downloading === id ? <Loader2 size={12} className="animate-spin" /> : <><Download size={12} /> PDF</>}
      </button>
    </div>
  );
}

function FileRow({ file, onDownload, downloading, testIdPrefix = "btn-download-file" }) {
  return (
    <div data-testid={`${testIdPrefix}-row-${file.file_id}`} className="flex items-start justify-between gap-3 py-3 border-b border-zinc-100 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-zinc-950 truncate inline-flex items-center gap-1.5">
          <Package size={13} className="text-zinc-700 flex-shrink-0" /> {file.filename}
        </div>
        {file.description && <div className="text-xs text-zinc-600 mt-1">{file.description}</div>}
        <div className="text-[11px] text-zinc-400 mt-0.5">{fmtSize(file.size_bytes)} · uploaded {fmtDate(file.created_at)}</div>
      </div>
      <button data-testid={`${testIdPrefix}-${file.file_id}`} onClick={() => onDownload(file)} disabled={downloading === file.file_id}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
        {downloading === file.file_id ? <Loader2 size={12} className="animate-spin" /> : <><Download size={12} /> Download</>}
      </button>
    </div>
  );
}

export default function CustomerProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    try {
      const res = await customerAxios().get(`${API}/customer/projects/${projectId}`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("lf_customer_session"); navigate("/customer/login"); return; }
      setError(e.response?.data?.detail || "Failed to load project");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const downloadInvoice = async (kind, id) => {
    setDownloading(id);
    try {
      const path = kind === "transaction"
        ? `${API}/customer/invoices/transaction/${id}/download`
        : `${API}/customer/invoices/charge/${id}/download`;
      const res = await customerAxios().get(path, { responseType: "blob" });
      downloadFromBlob(res.data, `invoice_${id}.pdf`);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not download invoice");
    } finally { setDownloading(null); }
  };

  const downloadFile = async (file) => {
    setDownloading(file.file_id);
    try {
      const res = await customerAxios().get(`${API}/customer/files/${file.file_id}/download`, { responseType: "blob" });
      downloadFromBlob(res.data, file.filename);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not download file");
    } finally { setDownloading(null); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="customer-project-detail">
      <Loader2 size={28} className="animate-spin text-zinc-400" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="text-center"><AlertTriangle size={28} className="text-red-500 mx-auto mb-3" /><p className="text-sm text-zinc-700">{error}</p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-blue-600 underline">Back to Dashboard</Link></div>
    </div>
  );

  const { project, transactions, charges, deliverables, chat_files } = data;
  const proposal = project.proposal;
  const price = proposal?.delivery_adjusted_price_usd;
  const paidCharges = (charges || []).filter(c => c.status === "paid");

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="customer-project-detail">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="btn-back-customer">
          <ArrowLeft size={16} />
        </button>
        <div className="w-8 h-8 bg-zinc-950 rounded-lg flex items-center justify-center"><Zap size={16} className="text-white" /></div>
        <div>
          <div className="font-bold text-zinc-900 text-sm">{project.project_id}</div>
          <div className="text-xs text-zinc-500">{project.industry} project</div>
        </div>
        <div className="ml-auto">
          <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_COLORS[project.status] || "bg-zinc-100"}`}>
            {STATUS_LABELS[project.status] || project.status}
          </span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        {/* Project header */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-6">
          <h1 className="text-2xl font-extrabold tracking-tight text-zinc-950">
            {proposal?.project_summary?.split(".")[0] || `${project.industry} project`}
          </h1>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-zinc-500">
            <span>{project.delivery_weeks}-week delivery</span>
            <span>·</span>
            <span>Created {fmtDate(project.created_at)}</span>
            {project.due_date && <><span>·</span><span className="inline-flex items-center gap-1"><Calendar size={11} /> Target {fmtDate(project.due_date)}</span></>}
          </div>
          {price && (
            <div className="mt-4 inline-flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-zinc-950">{project.currency === "eur" ? `€${(price * EUR_RATE).toFixed(0)}` : `$${price.toFixed(0)}`}</span>
              <span className="text-xs text-zinc-500">total project price</span>
            </div>
          )}
          {project.preview_url && (
            <a href={project.preview_url} target="_blank" rel="noopener noreferrer" data-testid="link-customer-preview"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full hover:bg-emerald-100">
              <ExternalLink size={12} /> View live preview
            </a>
          )}
          <div className="mt-4">
            <Link to={`/track/${project.project_id}`} data-testid="link-customer-track"
              className="text-sm text-zinc-600 hover:text-zinc-900 underline">Open chat & status →</Link>
          </div>
        </div>

        {/* Receipts */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="receipts-card">
          <h2 className="font-bold text-zinc-950 text-lg flex items-center gap-2 mb-1">
            <FileText size={18} className="text-zinc-700" /> Receipts & Invoices
          </h2>
          <p className="text-xs text-zinc-500 mb-3">Download a PDF receipt for any paid amount.</p>
          {(transactions || []).length === 0 && paidCharges.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No paid invoices yet.</p>
          ) : (
            <div data-testid="invoice-list">
              {(transactions || []).map(t => (
                <InvoiceRow key={t.transaction_id} kind="transaction" item={t} currency={project.currency} onDownload={downloadInvoice} downloading={downloading} />
              ))}
              {paidCharges.map(c => (
                <InvoiceRow key={c.charge_id} kind="charge" item={c} currency={project.currency} onDownload={downloadInvoice} downloading={downloading} />
              ))}
            </div>
          )}
        </div>

        {/* Deliverables */}
        <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="deliverables-card">
          <h2 className="font-bold text-zinc-950 text-lg flex items-center gap-2 mb-1">
            <Package size={18} className="text-zinc-700" /> Deliverables
          </h2>
          <p className="text-xs text-zinc-500 mb-3">Final assets, source code, and documentation shared by the FastPrototype.ai team.</p>
          {(deliverables || []).length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No deliverables shared yet. The team will upload them here as the project progresses.</p>
          ) : (
            <div data-testid="deliverables-list">
              {deliverables.map(f => (
                <FileRow key={f.file_id} file={f} onDownload={downloadFile} downloading={downloading} testIdPrefix="btn-download-deliverable" />
              ))}
            </div>
          )}
        </div>

        {/* Shared in chat */}
        {(chat_files || []).length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-3xl p-6" data-testid="chat-files-card">
            <h2 className="font-bold text-zinc-950 text-lg flex items-center gap-2 mb-1">
              <Paperclip size={18} className="text-zinc-700" /> Files Shared in Chat
            </h2>
            <p className="text-xs text-zinc-500 mb-3">Attachments exchanged in the project chat.</p>
            <div data-testid="chat-files-list">
              {chat_files.map(f => (
                <FileRow key={f.file_id} file={f} onDownload={downloadFile} downloading={downloading} testIdPrefix="btn-download-chat-file" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
